// Known SWIM-owned subdomains that should never be treated as merchant slugs
const SWIM_SUBDOMAINS = new Set([
  'www',
  'staging',
  'api',
  'api-staging',
  'merchant',
  'merchant-staging',
  'admin',
  'admin-staging',
  'status',
  'analytics',
  'help',
  'help-staging',
  'ops',
  'ops-staging',
  'docs',
  'docs-staging',
  // The full storefront (home, latest offers, categories) — reachable on its own host now
  // that the bare root domain serves the landing app. Without these, store.swimflows.com
  // matches the {slug} wildcard and renders a nonexistent "store" stream.
  'store',
  'store-staging',
  // Named cloudflared tunnel for local-dev phone testing — without this,
  // dev-tunnel.swimflows.com matches the {slug}.swimflows.com wildcard and
  // the app renders a nonexistent "dev-tunnel" stream in an escape-proof
  // loop (its "Back to Home" lands on "/", which is StreamPage again in
  // subdomain mode).
  'dev-tunnel',
])

// Staging stream hosts are {slug}-staging.swimflows.com (specs/036-canonical-stream-urls/staging-stream-hosts.md)
const STAGING_HOST_SUFFIX = '-staging'

/**
 * Returns the merchant stream slug if the current hostname is a merchant subdomain.
 * Returns null for all bare domains, known SWIM subdomains, and localhost.
 *
 * Handles:
 *   {slug}.swimflows.com          → slug  (production)
 *   {slug}-staging.swimflows.com  → slug  (staging stream host)
 *   {slug}.staging.swimflows.com  → slug  (staging)
 *   {slug}.lvh.me                 → slug  (local dev — lvh.me resolves *.lvh.me to 127.0.0.1)
 */
export function getSubdomainSlug(hostname: string): string | null {
  // Dev-only override: ?__slug=oak-olive-goods lets you test chromeless mode on localhost
  if (import.meta.env.DEV) {
    const override = new URLSearchParams(window.location.search).get('__slug')
    if (override) return override
  }

  // Raw IP or localhost — no subdomain
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null

  // lvh.me local dev: {slug}.lvh.me
  const lvhMatch = hostname.match(/^([^.]+)\.lvh\.me$/)
  if (lvhMatch) return lvhMatch[1]

  // Production: {slug}.swimflows.com
  // Staging:    {slug}.staging.swimflows.com
  const swimMatch = hostname.match(/^([^.]+)(?:\.staging)?\.swimflows\.com$/)
  if (!swimMatch) return null

  const sub = swimMatch[1]
  if (SWIM_SUBDOMAINS.has(sub)) return null

  // Staging stream hosts: {slug}-staging.swimflows.com. Strip one suffix to get the real slug.
  // The part before it must be non-empty and not a reserved SWIM name.
  if (sub.endsWith(STAGING_HOST_SUFFIX)) {
    const base = sub.slice(0, -STAGING_HOST_SUFFIX.length)
    if (base === '' || SWIM_SUBDOMAINS.has(base)) return null
    return base
  }

  return sub
}
